<template>
  <div class="card mb-25 border-0 rounded-0 bg-white add-user-card">
    <div class="card-body p-15 p-sm-20 p-md-25 p-lg-30 letter-spacing">
      <!-- Informations du contrat actuel -->
      <div class="card mb-4 border-info">
        <div class="card-header bg-info text-white">
          <h5 class="mb-0">
            <i class="flaticon-file me-2"></i>Informations du contrat actuel
          </h5>
        </div>
        <div class="card-body" v-if="contratActuel">
          <div class="row">
            <div class="col-md-4">
              <strong>Référence:</strong> {{ contratActuel.reference || 'N/A' }}
            </div>
            <div class="col-md-4">
              <strong>Emplacement:</strong> 
              <span v-if="contratActuel.codeemp">
                {{ contratActuel.codeemp.ncode_emp || contratActuel.codeemp.codeemp }}
              </span>
            </div>
            <div class="col-md-4">
              <strong>Usager actuel:</strong>
              <span v-if="contratActuel.matricule">
                {{ contratActuel.matricule.nom }} {{ contratActuel.matricule.prenom }}
                <br><small class="text-muted">{{ contratActuel.matricule.matricule }}</small>
              </span>
              <span v-else>{{ contratActuel.locataire || 'N/A' }}</span>
            </div>
            <div class="col-md-4 mt-2">
              <strong>Loyer:</strong> {{ formatNumber(contratActuel.loyer) }} FCFA
            </div>
            <div class="col-md-4 mt-2">
              <strong>Solde initial:</strong> 
              <span :class="contratActuel.type_solde === 'Avoir' ? 'text-success' : 'text-danger'">
                {{ formatNumber(contratActuel.solde_initial) }} FCFA
              </span>
              <br><small class="text-muted">({{ contratActuel.type_solde }})</small>
            </div>
            <div class="col-md-4 mt-2">
              <strong>Durée:</strong> {{ contratActuel.duree || 'N/A' }}
            </div>
          </div>
        </div>
        <div class="card-body" v-else>
          <div class="text-center">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Chargement...</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Formulaire de mutation -->
      <div class="card border-warning">
        <div class="card-header bg-warning text-dark">
          <h5 class="mb-0">
            <i class="flaticon-exchange me-2"></i>Formulaire de mutation
          </h5>
        </div>
        <div class="card-body">
          <Form @submit="effectuerMutation" :validation-schema="mutationSchema">
            <div class="row">
              <!-- Nouvel usager -->
              <div class="col-md-6 col-sm-12">
                <div class="form-group">
                  <label for="nouveau_matricule" class="form-label fw-medium text-black">
                    Nouvel usager <span class="text-danger">*</span>
                  </label>
                  <Field name="nouveau_matricule" v-model="nouveau_matricule" 
                    class="form-control shadow-none fs-md-15 text-black"
                    v-slot="{ field }">
                    <Multiselect v-model="field.value" v-bind="field" :filter-results="false" :min-chars="2"
                      :resolve-on-load="false" :delay="0" :searchable="true" :options-limit="300" 
                      :options="async (query) => {
                        if (query && query.length >= 2) {
                          const results = await fetchUsagerOptions(query);
                          if (results && results.length > 0) {
                            return results;
                          } else {
                            return [
                              { value: '', label: 'Aucun usager trouvé' },
                            ];
                          }
                        } else {
                          return [];
                        }
                      }
                        " noOptionsText="Tapez au moins deux caractères" placeholder="Rechercher le nouvel usager" />
                  </Field>
                  <ErrorMessage name="nouveau_matricule" class="text-danger" />
                </div>
              </div>

              <!-- Nouvelle référence -->
              <div class="col-md-6 col-sm-12">
                <div class="form-group">
                  <label class="form-label fw-medium text-black">
                    Nouvelle référence
                  </label>
                  <Field name="nouvelle_reference" type="text" v-model="nouvelle_reference"
                    class="form-control shadow-none fs-md-15 text-black" 
                    placeholder="Référence du nouveau contrat" />
                  <ErrorMessage name="nouvelle_reference" class="text-danger" />
                </div>
              </div>

              <!-- Nouveau solde initial -->
              <div class="col-md-4 col-sm-12">
                <div class="form-group">
                  <label class="form-label fw-medium text-black">
                    Nouveau solde initial (FCFA)
                  </label>
                  <Field name="nouveau_solde_initial" type="number" v-model="nouveau_solde_initial"
                    class="form-control shadow-none fs-md-15 text-black" 
                    placeholder="0" step="0.01" />
                  <small class="text-muted">Par défaut: {{ formatNumber(contratActuel?.solde_initial || 0) }} FCFA</small>
                  <ErrorMessage name="nouveau_solde_initial" class="text-danger" />
                </div>
              </div>

              <!-- Nouveau type de solde -->
              <div class="col-md-4 col-sm-12">
                <div class="form-group">
                  <label class="form-label fw-medium text-black">
                    Type de solde
                  </label>
                  <Field name="nouveau_type_solde" v-slot="{ field }" v-model="nouveau_type_solde">
                    <Multiselect :options="typeSoldeOptions" :searchable="false" 
                      v-model="field.value" v-bind="field"
                      placeholder="Sélectionner un type" />
                  </Field>
                  <ErrorMessage name="nouveau_type_solde" class="text-danger" />
                </div>
              </div>

              <!-- Motif de mutation -->
              <div class="col-md-4 col-sm-12">
                <div class="form-group">
                  <label class="form-label fw-medium text-black">
                    Motif de mutation <span class="text-danger">*</span>
                  </label>
                  <Field name="motif" type="text" v-model="motif"
                    class="form-control shadow-none fs-md-15 text-black" 
                    placeholder="Motif de la mutation" />
                  <ErrorMessage name="motif" class="text-danger" />
                </div>
              </div>

              <!-- Observation -->
              <div class="col-md-12 col-sm-12">
                <div class="form-group">
                  <label class="form-label fw-medium text-black">
                    Observation
                  </label>
                  <Field name="observation" v-model="observation" as="textarea" rows="3"
                    class="form-control shadow-none fs-md-15 text-black" 
                    placeholder="Observations sur la mutation" />
                  <ErrorMessage name="observation" class="text-danger" />
                </div>
              </div>

              <!-- Boutons -->
              <div class="col-md-12 mt-3">
                <div class="d-flex align-items-center">
                  <button class="btn btn-warning me-3" type="submit" :disabled="loading">
                    <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
                    <i class="flaticon-exchange me-2"></i>Effectuer la mutation
                  </button>
                  <router-link to="/liste-contrats" class="btn btn-secondary">
                    <i class="fa fa-times lh-1 me-1"></i>
                    Annuler
                  </router-link>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import ApiService from "@/services/ApiService";
import { useRouter, useRoute } from "vue-router";
import Multiselect from "@vueform/multiselect";
import Swal from "sweetalert2";

export default defineComponent({
  name: "MutationContrat",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const contratActuel = ref<any>(null);
    const nouveau_matricule = ref(null);
    const nouvelle_reference = ref("");
    const nouveau_solde_initial = ref<number | string>(0);
    const nouveau_type_solde = ref("Avoir");
    const motif = ref("");
    const observation = ref("");

    const typeSoldeOptions = [
      { value: "Avoir", label: "Avoir" },
      { value: "Dette", label: "Dette" },
    ];

    const mutationSchema = Yup.object().shape({
      nouveau_matricule: Yup.string().required("Le nouvel usager est obligatoire"),
      nouvelle_reference: Yup.string().notRequired(),
      nouveau_solde_initial: Yup.number().notRequired(),
      nouveau_type_solde: Yup.string().notRequired(),
      motif: Yup.string().required("Le motif de mutation est obligatoire"),
      observation: Yup.string().notRequired(),
    });

    // Charger les informations du contrat actuel
    const chargerContrat = async () => {
      const contratId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
      if (!contratId) {
        await Swal.fire({
          icon: "error",
          title: "Erreur",
          text: "Aucun contrat sélectionné.",
        });
        router.push("/liste-contrats");
        return;
      }

      try {
        const response = await ApiService.get("/contrats", contratId as string);
        if (response.data && response.data.success) {
          contratActuel.value = response.data.data;
          // Pré-remplir avec les valeurs du contrat actuel
          nouveau_solde_initial.value = response.data.data.solde_initial || 0;
          nouveau_type_solde.value = response.data.data.type_solde || "Avoir";
        } else {
          throw new Error("Contrat non trouvé");
        }
      } catch (error: any) {
        console.error("Erreur lors du chargement du contrat:", error);
        await Swal.fire({
          icon: "error",
          title: "Erreur",
          text: error.response?.data?.message || "Impossible de charger le contrat.",
        });
        router.push("/liste-contrats");
      }
    };

    const fetchUsagerOptions = async (query: string) => {
      try {
        const response = await ApiService.post("getUsagerByKey", { recherche: query });
        if (response.data && response.data.data) {
          return response.data.data.map((usager: any) => ({
            value: usager.matricule,
            label: `${usager.matricule} || ${usager.nom} ${usager.prenom} ${usager.tel ? " - " + usager.tel : ""}`,
          }));
        }
        return [];
      } catch (error) {
        console.error("Erreur lors de la recherche d'usager:", error);
        return [];
      }
    };

    const effectuerMutation = async (values: any) => {
      loading.value = true;
      try {
        const mutationData = {
          nouveau_matricule: nouveau_matricule.value,
          nouvelle_reference: nouvelle_reference.value || null,
          nouveau_solde_initial: typeof nouveau_solde_initial.value === 'string' 
            ? parseFloat(nouveau_solde_initial.value) 
            : nouveau_solde_initial.value,
          nouveau_type_solde: nouveau_type_solde.value,
          motif: motif.value,
          observation: observation.value || null,
        };

        const contratId = Array.isArray(route.params.id) ? route.params.id[0] : route.params.id;
        const response = await ApiService.post(`/contrats/${contratId}/muter`, mutationData);

        if (response.data && response.data.success) {
          await Swal.fire({
            icon: "success",
            title: "Succès",
            text: response.data.message || "La mutation a été effectuée avec succès.",
            confirmButtonText: "OK",
          });
          router.push("/liste-contrats");
        } else {
          throw new Error(response.data.message || "Erreur lors de la mutation");
        }
      } catch (error: any) {
        console.error("Erreur lors de la mutation:", error);
        await Swal.fire({
          icon: "error",
          title: "Erreur",
          text: error.response?.data?.message || error.message || "Une erreur est survenue lors de la mutation.",
          confirmButtonText: "OK",
        });
      } finally {
        loading.value = false;
      }
    };

    const formatNumber = (value: number) => {
      if (!value) return "0";
      return new Intl.NumberFormat("fr-FR", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    };

    onMounted(() => {
      chargerContrat();
    });

    return {
      loading,
      contratActuel,
      nouveau_matricule,
      nouvelle_reference,
      nouveau_solde_initial,
      nouveau_type_solde,
      motif,
      observation,
      typeSoldeOptions,
      mutationSchema,
      fetchUsagerOptions,
      effectuerMutation,
      formatNumber,
    };
  },
});
</script>

